import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, Upload, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const humanize = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").filter(Boolean).join("; ");
  return e?.message || "Ошибка";
};

/** Отправка одобренного шаблона Meta — единственный способ написать клиенту,
 *  когда 24-часовое окно свободной переписки закрылось. */
export const MetaTemplateSend = ({ clientId, clientName, onClose, onSent }) => {
  const [items, setItems] = useState([]);
  const [key, setKey] = useState("");
  const [params, setParams] = useState([]);
  const [header, setHeader] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const tpl = items.find(t => `${t.name}|${t.language}` === key);
  const needsMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(tpl?.header_format);

  useEffect(() => {
    axios.get(`${API}/admin/waba/templates`, { withCredentials: true })
      .then(r => setItems(r.data.items || []))
      .catch(e => setError(humanize(e)));
  }, []);

  const pick = (value) => {
    setKey(value); setParams([]);
    const t = items.find(x => `${x.name}|${x.language}` === value);
    setHeader(t?.last_header_value || "");
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/admin/waba/media/upload`, fd, { withCredentials: true });
      setHeader(r.data.url);
    } catch (e2) { setError(humanize(e2)); } finally { setBusy(false); }
  };

  const send = async () => {
    if (!tpl) return;
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/admin/whatsapp/send-template`, {
        client_id: clientId, template_name: tpl.name, language: tpl.language,
        header_type: needsMedia ? tpl.header_format.toLowerCase()
          : tpl.header_format === "TEXT" ? "text" : "none",
        header_value: header,
        params: params.slice(0, tpl.params_count),
        preview_text: tpl.body || `[шаблон: ${tpl.name}]`,
      }, { withCredentials: true });
      onSent?.();
      onClose();
    } catch (e) { setError(humanize(e)); } finally { setBusy(false); }
  };

  const inp = { width: "100%", border: "1px solid #e5e7eb", borderRadius: 6,
                padding: "9px 11px", fontFamily: "Manrope", fontSize: 13,
                outline: "none", boxSizing: "border-box" };

  return (
    <div data-testid="meta-template-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 3000, padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", borderRadius: 12, padding: 20, width: "100%",
                  maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
            Шаблон для {clientName || "клиента"}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
                                              cursor: "pointer", color: "#6b7280" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#6b7280",
                       margin: "8px 0 14px", lineHeight: 1.5 }}>
          Клиент не писал больше 24 часов — Meta пропустит только одобренный шаблон.
          После его ответа снова можно писать свободным текстом.
        </div>

        <select data-testid="meta-template-select" value={key}
          onChange={e => pick(e.target.value)} style={inp}>
          <option value="">Выберите шаблон…</option>
          {items.map(t => (
            <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
              {t.name} · {t.language}
            </option>
          ))}
        </select>

        {tpl?.body && (
          <div style={{ background: "#F9FAFB", border: "1px solid #f1f5f9", borderRadius: 8,
                         padding: "10px 12px", marginTop: 10, fontFamily: "Manrope",
                         fontSize: 12.5, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {tpl.body}
          </div>
        )}

        {Array.from({ length: tpl?.params_count || 0 }).map((_, i) => (
          <input key={i} data-testid={`meta-template-param-${i + 1}`} value={params[i] || ""}
            onChange={e => setParams(p => { const n = [...p]; n[i] = e.target.value; return n; })}
            placeholder={`Значение {{${i + 1}}}`} style={{ ...inp, marginTop: 8 }} />
        ))}

        {needsMedia && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input ref={fileRef} type="file" hidden onChange={upload} accept="image/*,video/*" />
              <button data-testid="meta-template-upload" onClick={() => fileRef.current?.click()}
                disabled={busy}
                style={{ background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                          padding: "9px 12px", cursor: "pointer", fontFamily: "Manrope",
                          fontSize: 12.5, display: "flex", alignItems: "center", gap: 6 }}>
                <Upload size={13} /> {header ? "Заменить картинку" : "Загрузить картинку"}
              </button>
              {header && <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#047857" }}>✓ готово</span>}
            </div>
            {header && (
              <img src={header} alt="" style={{ marginTop: 8, width: "100%", maxHeight: 140,
                    objectFit: "cover", borderRadius: 6 }} />
            )}
          </div>
        )}

        {error && (
          <div data-testid="meta-template-error"
            style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
                      borderRadius: 6, padding: "8px 10px", fontFamily: "Manrope",
                      fontSize: 12, marginTop: 10 }}>{error}</div>
        )}

        <button data-testid="meta-template-send" onClick={send}
          disabled={busy || !tpl || (needsMedia && !header)}
          style={{ marginTop: 14, width: "100%",
                    background: (!tpl || (needsMedia && !header)) ? "#e5e7eb"
                      : "linear-gradient(135deg,#25D366,#128C7E)",
                    color: (!tpl || (needsMedia && !header)) ? "#9ca3af" : "white",
                    border: "none", borderRadius: 6, padding: "12px",
                    cursor: busy ? "wait" : "pointer", fontFamily: "Manrope",
                    fontWeight: 700, fontSize: 13, display: "flex",
                    alignItems: "center", justifyContent: "center", gap: 7 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          Отправить шаблон
        </button>
      </div>
    </div>
  );
};

export default MetaTemplateSend;
